import { apiClient } from "./client";
import { MOCKS_ENABLED } from "./mock/registry";
import { mockPatients } from "./mock/patients";
import type {
  AlertCreateRequest,
  AlertResponse,
  AllergyCreateRequest,
  AllergyOverrideReason,
  AllergyResponse,
  AllergyUpdateRequest,
  ConditionCreateRequest,
  ConditionResponse,
  ConditionUpdateRequest,
  DrugAllergyWarning,
  EncounterAllergyConflict,
  PageResponse,
  PatientCreateRequest,
  PatientCreateResponse,
  PatientDuplicateWarning,
  PatientListFilter,
  PatientIdentifierResponse,
  PatientResponse,
  PatientSearchResult,
  PatientSummaryResponse,
  PatientUpdateRequest,
  TagsResponse,
} from "../types/patient";

const BASE = "/v1/patients";

function buildParams(filter: PatientListFilter | undefined, pageable: { page?: number; size?: number; sort?: string }) {
  const params: Record<string, string | number> = {};
  if (filter) {
    if (filter.q) params.q = filter.q;
    if (filter.status) params.status = filter.status;
    if (filter.gender) params.gender = filter.gender;
    if (filter.provinceCode) params.provinceCode = filter.provinceCode;
    if (filter.primaryBranchId) params.primaryBranchId = filter.primaryBranchId;
    if (filter.primaryDoctorId) params.primaryDoctorId = filter.primaryDoctorId;
    if (filter.ageMin != null) params.ageMin = filter.ageMin;
    if (filter.ageMax != null) params.ageMax = filter.ageMax;
  }
  if (pageable.page != null) params.page = pageable.page;
  if (pageable.size != null) params.size = pageable.size;
  if (pageable.sort) params.sort = pageable.sort;
  return params;
}

const realPatientsApi = {
  list(filter: PatientListFilter | undefined, pageable: { page?: number; size?: number; sort?: string }) {
    return apiClient
      .get<PageResponse<PatientSummaryResponse>>(BASE, { params: buildParams(filter, pageable) })
      .then((r) => r.data);
  },

  search(q: string, pageable: { page?: number; size?: number }) {
    return apiClient
      .get<PageResponse<PatientSearchResult>>(`${BASE}/search`, {
        params: { q, ...pageable },
      })
      .then((r) => r.data);
  },

  get(id: string) {
    return apiClient.get<PatientResponse>(`${BASE}/${id}`).then((r) => r.data);
  },

  create(req: PatientCreateRequest) {
    return apiClient.post<PatientCreateResponse>(BASE, req).then((r) => r.data);
  },

  checkDuplicates(probe: {
    fullName: string;
    phone?: string | null;
    dateOfBirth?: string | null;
    cccd?: string | null;
  }) {
    return apiClient
      .post<PatientDuplicateWarning | "">(`${BASE}/check-duplicates`, probe)
      .then((r) => (r.status === 204 ? null : (r.data as PatientDuplicateWarning)));
  },

  update(id: string, req: PatientUpdateRequest) {
    return apiClient.put<PatientResponse>(`${BASE}/${id}`, req).then((r) => r.data);
  },

  archive(id: string) {
    return apiClient.post<void>(`${BASE}/${id}/archive`).then((r) => r.data);
  },

  restore(id: string) {
    return apiClient.post<void>(`${BASE}/${id}/restore`).then((r) => r.data);
  },

  markDeceased(id: string, deceasedAt: string) {
    return apiClient.post<void>(`${BASE}/${id}/deceased`, { deceasedAt }).then((r) => r.data);
  },

  softDelete(id: string) {
    return apiClient.delete<void>(`${BASE}/${id}`).then((r) => r.data);
  },

  /** Hard-delete a patient (cascades appointments + encounters). Frozen contract: 204. */
  remove(id: string): Promise<void> {
    return apiClient.delete(`${BASE}/${id}/permanent`).then(() => undefined);
  },

  // ---- allergies ----
  listAllergies(patientId: string, includeInactive = false) {
    return apiClient
      .get<AllergyResponse[]>(`${BASE}/${patientId}/allergies`, {
        params: { includeInactive },
      })
      .then((r) => r.data);
  },
  createAllergy(patientId: string, req: AllergyCreateRequest) {
    return apiClient.post<void>(`${BASE}/${patientId}/allergies`, req).then((r) => r.data);
  },
  updateAllergy(patientId: string, allergyId: string, req: AllergyUpdateRequest) {
    return apiClient.put<void>(`${BASE}/${patientId}/allergies/${allergyId}`, req).then((r) => r.data);
  },
  deleteAllergy(patientId: string, allergyId: string) {
    return apiClient.delete<void>(`${BASE}/${patientId}/allergies/${allergyId}`).then((r) => r.data);
  },

  /**
   * Which of the patient's active DRUG allergies a medicine matches (#18859). Empty = no warning.
   * Asked when the doctor picks the medicine, so the answer arrives before the line exists.
   */
  checkDrugAllergy(
    patientId: string,
    medicineId: string,
    encounterId?: string
  ): Promise<DrugAllergyWarning[]> {
    return apiClient
      .get<DrugAllergyWarning[]>(`${BASE}/${patientId}/drug-allergy-check`, {
        params: { medicineId, encounterId },
      })
      .then((r) => r.data);
  },
  /**
   * The doctor saw the warning and is prescribing anyway, with a reason (#18859 §5).
   *
   * Not optional politeness: until this is recorded, the backend refuses to finalize the
   * prescription at all. The dialog is UX; this row is what actually unblocks the visit.
   */
  /**
   * Every drug-allergy conflict on this VISIT's prescription, each marked answered or not (#19184).
   * Counts an answer given on the visit OR on the Đơn thuốc screen — the same question the server
   * asks before it signs the visit.
   */
  encounterAllergyConflicts(
    patientId: string,
    encounterId: string,
    medicineIds: string[]
  ): Promise<EncounterAllergyConflict[]> {
    if (medicineIds.length === 0) return Promise.resolve([]);
    const params = new URLSearchParams({ encounterId });
    for (const m of medicineIds) params.append("medicineId", m);
    return apiClient
      .get<EncounterAllergyConflict[]>(`${BASE}/${patientId}/drug-allergy-check/encounter`, {
        params,
      })
      .then((r) => r.data);
  },
  overrideDrugAllergy(
    patientId: string,
    body: {
      medicineId: string;
      allergyId: string;
      encounterId?: string | null;
      prescriptionId?: string | null;
      reasonCode: AllergyOverrideReason;
      reasonText?: string | null;
      warnedAt?: string | null;
    }
  ): Promise<void> {
    return apiClient
      .post<void>(`${BASE}/${patientId}/drug-allergy-check/override`, body)
      .then(() => undefined);
  },

  // ---- conditions ----
  listConditions(patientId: string, includeInactive = false) {
    return apiClient
      .get<ConditionResponse[]>(`${BASE}/${patientId}/conditions`, {
        params: { includeInactive },
      })
      .then((r) => r.data);
  },
  createCondition(patientId: string, req: ConditionCreateRequest) {
    return apiClient.post<void>(`${BASE}/${patientId}/conditions`, req).then((r) => r.data);
  },
  updateCondition(patientId: string, conditionId: string, req: ConditionUpdateRequest) {
    return apiClient.put<void>(`${BASE}/${patientId}/conditions/${conditionId}`, req).then((r) => r.data);
  },
  deleteCondition(patientId: string, conditionId: string) {
    return apiClient.delete<void>(`${BASE}/${patientId}/conditions/${conditionId}`).then((r) => r.data);
  },

  // ---- identifiers (CCCD history + BHYT) ----
  listIdentifiers(patientId: string) {
    return apiClient
      .get<PatientIdentifierResponse[]>(`${BASE}/${patientId}/identifiers`)
      .then((r) => r.data);
  },
  addCccd(
    patientId: string,
    req: { value: string; issuedBy?: string | null; issuedAt?: string | null },
  ) {
    return apiClient
      .post<void>(`${BASE}/${patientId}/identifiers/cccd`, req)
      .then((r) => r.data);
  },
  setBhyt(patientId: string, value: string) {
    return apiClient
      .put<void>(`${BASE}/${patientId}/identifiers/bhyt`, { value })
      .then((r) => r.data);
  },

  // ---- alerts ----
  listAlerts(patientId: string) {
    return apiClient.get<AlertResponse[]>(`${BASE}/${patientId}/alerts`).then((r) => r.data);
  },
  createAlert(patientId: string, req: AlertCreateRequest) {
    return apiClient.post<void>(`${BASE}/${patientId}/alerts`, req).then((r) => r.data);
  },
  acknowledgeAlert(patientId: string, alertId: string) {
    return apiClient.post<void>(`${BASE}/${patientId}/alerts/${alertId}/acknowledge`).then((r) => r.data);
  },
  dismissAlert(patientId: string, alertId: string) {
    return apiClient.delete<void>(`${BASE}/${patientId}/alerts/${alertId}`).then((r) => r.data);
  },

  // ---- tags ----
  listTags(patientId: string) {
    return apiClient.get<TagsResponse>(`${BASE}/${patientId}/tags`).then((r) => r.data);
  },
  replaceTags(patientId: string, tags: string[]) {
    return apiClient.put<TagsResponse>(`${BASE}/${patientId}/tags`, { tags }).then((r) => r.data);
  },
};

// Routes to the mock adapter until the real `/api/v1/patients` is wired
// end-to-end (auth + tenant). Flip via `VITE_USE_MOCKS=false`.
export const patientsApi = MOCKS_ENABLED ? mockPatients : realPatientsApi;

export const patientQueryKeys = {
  all: ["patients"] as const,
  list: (filter: PatientListFilter | undefined, page: number) =>
    ["patients", "list", filter ?? {}, page] as const,
  search: (q: string, page: number) => ["patients", "search", q, page] as const,
  detail: (id: string) => ["patients", "detail", id] as const,
  allergies: (id: string, includeInactive: boolean) =>
    ["patients", id, "allergies", includeInactive] as const,
  conditions: (id: string, includeInactive: boolean) =>
    ["patients", id, "conditions", includeInactive] as const,
  alerts: (id: string) => ["patients", id, "alerts"] as const,
  tags: (id: string) => ["patients", id, "tags"] as const,
};

/**
 * Duplicate review + merge (#18862).
 *
 * Separate from `patientsApi` because these are administrative operations on the identity of a
 * record rather than on its content, and merge is gated on its own `patient.merge` permission.
 */
export interface DuplicateSide {
  id: string;
  patientCode: string | null;
  fullName: string;
  dateOfBirth: string | null;
  gender: string | null;
  phone: string | null;
  nationalId: string | null;
  insuranceNo: string | null;
  address: string | null;
  status: string;
}

export interface DuplicateCandidate {
  id: string;
  confidence: "STRONG" | "LIKELY" | "WEAK";
  score: number;
  reasons: string | null;
  primary: DuplicateSide | null;
  candidate: DuplicateSide | null;
}

export const patientMergeApi = {
  listCandidates(page = 0, size = 20) {
    return apiClient
      .get<{ content: DuplicateCandidate[]; totalElements: number }>(
        "/v1/patients/duplicates",
        { params: { page, size } }
      )
      .then((r) => r.data);
  },
  dismiss(candidateId: string, notes?: string) {
    return apiClient
      .post<void>(`/v1/patients/duplicates/${candidateId}/dismiss`, { notes: notes ?? null })
      .then(() => undefined);
  },
  /** `targetId` survives; `sourceId` becomes MERGED and points at it. Irreversible. */
  merge(sourceId: string, targetId: string, reason?: string) {
    return apiClient
      .post<void>("/v1/patients/duplicates/merge", {
        sourceId,
        targetId,
        reason: reason ?? null,
      })
      .then(() => undefined);
  },
};
